import { Icon } from '@iconify/react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSystemBarsStyle, systemBarsIconsForBackground } from '@/hooks/useSystemBarsStyle';
import api from '@/services/apiService';
import {
  getHostMabarBackFallback,
  historyCanGoBack,
} from '@/utils/nativeNavigation';
import { navigateBackToGameRekap } from '@/utils/hostMabarNavigation';
import { MenuItemConfig } from './menuConfig';
import { getAdminBasePath } from '@/utils/adminPaths';

function getCommunityHeaderTitle(pathname: string): {
  title: string;
  subtitle?: string;
  isSubpage: boolean;
  isWhiteHeader: boolean;
} {
  if (pathname.includes('/game/create')) {
    return { title: 'Buat Game Baru', isSubpage: true, isWhiteHeader: true };
  }
  if (pathname.includes('/game/') && pathname.includes('/edit')) {
    return { title: 'Edit Game', isSubpage: true, isWhiteHeader: true };
  }
  if (pathname.startsWith('/host/mabar/create')) {
    return { title: 'Buat Event Mabar', isSubpage: true, isWhiteHeader: true };
  }
  if (pathname.includes('/edit-info')) {
    return { title: 'Edit Info Dasar', isSubpage: true, isWhiteHeader: true };
  }
  if (pathname.includes('/edit')) {
    return { title: 'Edit Event', isSubpage: true, isWhiteHeader: true };
  }
  if (pathname.includes('/statistic')) {
    return { title: 'Statistik Event', isSubpage: true, isWhiteHeader: true };
  }
  if (pathname.includes('/badminton/tambah-game')) {
    return { title: 'Tambah Game', isSubpage: true, isWhiteHeader: true };
  }
  if (pathname.includes('/badminton/game/') && pathname.includes('/skor')) {
    return { title: 'Edit Skor', isSubpage: true, isWhiteHeader: true };
  }
  if (pathname.includes('/padel/tambah-game')) {
    return { title: 'Tambah Game Padel', isSubpage: true, isWhiteHeader: true };
  }
  if (pathname.includes('/padel/game/') && pathname.includes('/skor')) {
    return { title: 'Edit Skor', isSubpage: true, isWhiteHeader: true };
  }
  if (pathname.includes('/badminton')) {
    return { title: 'Statistik Event', isSubpage: true, isWhiteHeader: true };
  }
  if (pathname.includes('/padel')) {
    return { title: 'Statistik Event', isSubpage: true, isWhiteHeader: true };
  }
  if (pathname.includes('/pickleball')) {
    return { title: 'Statistik Event', isSubpage: true, isWhiteHeader: true };
  }
  if (pathname.includes('/dashboard')) {
    return { title: 'Dashboard Event', isSubpage: true, isWhiteHeader: true };
  }
  if (pathname.startsWith('/host/mabar/') && pathname !== '/host/mabar') {
    return { title: 'Detail Game', isSubpage: true, isWhiteHeader: true };
  }
  if (pathname === '/host/mabar' || pathname === '/host/mabar/') {
    return { title: 'Event', isSubpage: false, isWhiteHeader: false };
  }
  if (pathname.startsWith('/host/members')) {
    return { title: 'Member', isSubpage: false, isWhiteHeader: false };
  }
  if (pathname.startsWith('/host/profile-settings')) {
    return { title: 'Profil', isSubpage: false, isWhiteHeader: false };
  }
  if (pathname.startsWith('/host/menu')) {
    return { title: 'Menu Komunitas', isSubpage: true, isWhiteHeader: true };
  }
  if (pathname.startsWith('/host/hosts')) {
    return {
      title: 'Host Management',
      subtitle: 'Player dengan akses kelola komunitas',
      isSubpage: true,
      isWhiteHeader: true,
    };
  }
  if (pathname.startsWith('/host/cash') || pathname === '/cash' || pathname.startsWith('/cash')) {
    return {
      title: 'Cashflow Management',
      subtitle: 'Pemasukan event ikut status pembayaran',
      isSubpage: true,
      isWhiteHeader: true,
    };
  }
  if (pathname.startsWith('/host/wallet')) {
    return { title: 'Dompet Komunitas', isSubpage: true, isWhiteHeader: true };
  }
  if (pathname.startsWith('/host/transactions')) {
    return { title: 'Riwayat Transaksi', isSubpage: true, isWhiteHeader: true };
  }
  if (pathname.startsWith('/host/whatsapp')) {
    return { title: 'WhatsApp Notification', isSubpage: true, isWhiteHeader: true };
  }
  if (pathname === '/host' || pathname === '/host/') {
    return { title: 'Home', isSubpage: false, isWhiteHeader: false };
  }
  return { title: 'Komunitas', isSubpage: false, isWhiteHeader: false };
}

const MobileTopbar: React.FC<{ menuItems: MenuItemConfig[] }> = ({ menuItems }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [communityName, setCommunityName] = useState<string>('');
  const [communityAvatar, setCommunityAvatar] = useState<string>('');
  const [rekapState, setRekapState] = useState<{ isFinalizedGame: boolean; isPushingMmr: boolean }>({
    isFinalizedGame: false,
    isPushingMmr: false,
  });

  const [dynamicSubtitle, setDynamicSubtitle] = useState<string | null>(null);

  const isCommunity =
    location.pathname.startsWith('/host') ||
    location.pathname === '/cash' ||
    location.pathname.startsWith('/cash');
  const isRekapMainPage = Boolean(
    location.pathname.match(/\/host\/mabar\/game\/([^/]+)\/rekap\/?$/)
  );
  const { title, subtitle, isSubpage, isWhiteHeader } = getCommunityHeaderTitle(location.pathname);
  const activeSubtitle = dynamicSubtitle ?? subtitle;

  const isHostProfile = location.pathname.startsWith('/host/profile-settings');
  const isLightStatusBarSurface = !isCommunity || isWhiteHeader || isHostProfile;
  useSystemBarsStyle(systemBarsIconsForBackground(isLightStatusBarSurface ? 'light' : 'dark'));

  useEffect(() => {
    setDynamicSubtitle(null);
    const handleSubtitle = (e: Event) => {
      const customEv = e as CustomEvent<{ subtitle?: string | null }>;
      if (customEv.detail && 'subtitle' in customEv.detail) {
        setDynamicSubtitle(customEv.detail.subtitle ?? null);
      }
    };

    window.addEventListener('sporto-topbar-subtitle', handleSubtitle);
    window.dispatchEvent(new Event('sporto-request-topbar-subtitle'));

    return () => {
      window.removeEventListener('sporto-topbar-subtitle', handleSubtitle);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (isCommunity) {
      const storedName = localStorage.getItem('communityName') || '';
      const storedAvatar = localStorage.getItem('communityAvatar') || '';
      if (storedName) setCommunityName(storedName);
      if (storedAvatar) setCommunityAvatar(storedAvatar);

      // Fetch fresh profile data to populate name and logo right after login
      const fetchProfile = async () => {
        try {
          const res = await api.get('/community/profile');
          if (res.data?.data) {
            const comm = res.data.data.community || res.data.data;
            const cName = comm.name || '';
            const cAvatar = comm.assets?.logo || comm.logo || comm.avatar || '';
            if (cName) {
              localStorage.setItem('communityName', cName);
              setCommunityName(cName);
            }
            if (cAvatar) {
              localStorage.setItem('communityAvatar', cAvatar);
              setCommunityAvatar(cAvatar);
            }
          }
        } catch (err) {
          console.error('Failed to fetch community profile in MobileTopbar:', err);
        }
      };

      fetchProfile();
    }
  }, [isCommunity, location.pathname]);

  useEffect(() => {
    if (!isRekapMainPage) return;

    const handleState = (e: Event) => {
      const customEv = e as CustomEvent<{ isFinalizedGame: boolean; isPushingMmr: boolean }>;
      if (customEv.detail) {
        setRekapState(customEv.detail);
      }
    };

    window.addEventListener('sporto-rekap-state', handleState);
    window.dispatchEvent(new Event('sporto-request-rekap-state'));

    return () => {
      window.removeEventListener('sporto-rekap-state', handleState);
    };
  }, [isRekapMainPage, location.pathname]);

  // Find active parent for admin tab menus
  const activeParent = menuItems.find(
    (item) =>
      (location.pathname.startsWith(item.path) &&
        item.path !== '/admin' &&
        item.path !== '/' &&
        item.path !== '/client' &&
        item.path !== '/host') ||
      location.pathname === item.path
  );

  const hasChildren = activeParent?.children && activeParent.children.length > 0;

  const handleBack = () => {
    if (location.pathname.startsWith('/host/menu')) {
      navigate('/host/profile-settings');
      return;
    }
    if (location.pathname.startsWith('/host/hosts')) {
      navigate('/host/menu');
      return;
    }
    if (location.pathname.startsWith('/host/cash') || location.pathname === '/cash' || location.pathname.startsWith('/cash')) {
      if (location.search.includes('view=transactions') || location.search.includes('view=detail')) {
        navigate(location.pathname);
        return;
      }
      navigate('/host/menu');
      return;
    }
    if (location.pathname.startsWith('/host/mabar')) {
      if (historyCanGoBack()) {
        navigate(-1);
        return;
      }
      navigate(getHostMabarBackFallback(location.pathname));
      return;
    }

    navigate(-1);
  };

  // Custom Topbar for Community routes
  if (isCommunity) {
    if (
      location.pathname === '/host/profile-settings' ||
      location.pathname === '/host/profile-settings/' ||
      location.pathname.endsWith('/hasil')
    ) {
      return null;
    }

    if (location.pathname.includes('/rekap/select-assist')) {
      return (
        <header className="fixed top-0 left-0 right-0 z-40 bg-white text-[#0E1726] border-b border-[#E0E6ED] shadow-2xs pt-safe md:hidden">
          <div className="flex items-center justify-between px-4 h-14 gap-3">
            <button
              type="button"
              onClick={() => {
                navigate(-1);
              }}
              className="p-1 -ml-1 rounded-full text-[#0E1726] hover:bg-gray-100 transition-colors cursor-pointer active:scale-95"
              aria-label="Kembali"
            >
              <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
            </button>

            <div className="flex-1 min-w-0">
              <h1 className="text-base font-semibold text-[#0E1726] truncate">Assist</h1>
              <p className="text-xs font-semibold text-gray-400 truncate">
                Opsional · lewati kalau tidak ada
              </p>
            </div>

            <div className="w-9 h-9 rounded-2xl flex items-center justify-center shrink-0 bg-orange-50 text-[#FC480D]">
              <Icon icon="ph:hand-heart-bold" className="w-4.5 h-4.5" />
            </div>
          </div>
        </header>
      );
    }

    if (location.pathname.includes('/rekap/select-player')) {
      const searchParams = new URLSearchParams(location.search);
      const eventType = searchParams.get('event') || 'freestyle';
      const rawTime = searchParams.get('time') || searchParams.get('timeTag') || "0'";
      const timeTag = rawTime.endsWith("'") ? rawTime : `${rawTime}'`;
      const match = location.pathname.match(/\/host\/mabar\/game\/([^/]+)\/rekap\/select-player/);
      const gameSlug = match ? match[1] : '';

      const EVENT_CONFIGS: Record<string, { label: string; icon: string; iconBg: string }> = {
        goal: { label: 'Gol', icon: 'ph:soccer-ball-bold', iconBg: 'bg-orange-50 text-[#FC480D]' },
        own_goal: { label: 'Gol Bunuh Diri (Own Goal)', icon: 'ph:arrows-clockwise-bold', iconBg: 'bg-red-100 text-red-600' },
        save: { label: 'Penyelamatan (Save)', icon: 'ph:shield-check-bold', iconBg: 'bg-emerald-100 text-emerald-600' },
        clearance: { label: 'Clearance', icon: 'ph:shield-bold', iconBg: 'bg-blue-100 text-blue-600' },
        key_pass: { label: 'Key Pass', icon: 'ph:arrow-elbow-down-right-bold', iconBg: 'bg-purple-100 text-purple-600' },
        freestyle: { label: 'Freestyle', icon: 'ph:sparkle-bold', iconBg: 'bg-amber-100 text-amber-600' },
        foul: { label: 'Pelanggaran (Foul)', icon: 'ph:warning-bold', iconBg: 'bg-amber-100 text-amber-600' },
        yellow_card: { label: 'Kartu Kuning', icon: 'ph:rectangle-bold', iconBg: 'bg-amber-100 text-amber-500' },
        red_card: { label: 'Kartu Merah', icon: 'ph:rectangle-bold', iconBg: 'bg-red-100 text-red-500' },
        substitute: { label: 'Pergantian Pemain', icon: 'ph:arrows-left-right-bold', iconBg: 'bg-blue-100 text-blue-600' },
      };

      const eventConfig = EVENT_CONFIGS[eventType] || EVENT_CONFIGS.freestyle;

      return (
        <header className="fixed top-0 left-0 right-0 z-40 bg-white text-[#0E1726] border-b border-[#E0E6ED] shadow-2xs pt-safe md:hidden">
          <div className="flex items-center justify-between px-4 h-14 gap-3">
            <button
              type="button"
              onClick={() => {
                if (gameSlug) navigateBackToGameRekap(navigate, gameSlug, 1);
                else navigate(-1);
              }}
              className="p-1 -ml-1 rounded-full text-[#0E1726] hover:bg-gray-100 transition-colors cursor-pointer active:scale-95"
              aria-label="Kembali"
            >
              <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
            </button>

            <div className="flex-1 min-w-0">
              <h1 className="text-base font-semibold text-[#0E1726] truncate">{eventConfig.label}</h1>
              <p className="text-xs font-semibold text-gray-400 truncate">
                Pilih pemain · menit {timeTag}
              </p>
            </div>

            <div className={`w-9 h-9 rounded-2xl flex items-center justify-center shrink-0 ${eventConfig.iconBg}`}>
              <Icon icon={eventConfig.icon} className="w-4.5 h-4.5" />
            </div>
          </div>
        </header>
      );
    }
    const isHome = location.pathname === '/host' || location.pathname === '/host/';

    return (
      <header
        className={`fixed top-0 left-0 right-0 z-40 shadow-2xs pt-safe md:hidden ${
          isWhiteHeader
            ? 'bg-white text-[#0E1726] border-b border-[#E0E6ED]'
            : 'bg-[#FC480D] text-white shadow-sm'
        }`}
      >
        <div className="flex items-center justify-between px-4 h-14">
          {/* Left Side: Back button or Title/Brand */}
          <div className="flex items-center gap-3 min-w-0">
            {isSubpage ? (
              <button
                type="button"
                onClick={handleBack}
                className={`p-1 -ml-1 rounded-full transition-transform cursor-pointer active:scale-95 ${
                  isWhiteHeader
                    ? 'text-[#0E1726] hover:bg-gray-100'
                    : 'text-white hover:bg-white/10'
                }`}
                aria-label="Kembali"
              >
                <Icon icon="ph:arrow-left" className="w-5 h-5" />
              </button>
            ) : isHome && communityAvatar ? (
              <img
                src={communityAvatar}
                alt={communityName}
                className="w-9 h-9 rounded-full object-cover border-2 border-white/60 flex-shrink-0"
              />
            ) : null}

            <div className="flex flex-col min-w-0">
              <h1
                className={`text-lg font-semibold truncate py-0.5 leading-tight ${
                  isWhiteHeader ? 'text-[#0E1726]' : 'text-white'
                }`}
              >
                {isHome && communityName ? communityName : title}
              </h1>
              {activeSubtitle && (
                <p className="text-[11px] font-medium text-gray-400 truncate leading-tight">
                  {activeSubtitle}
                </p>
              )}
            </div>
          </div>

          {/* Right Side: Push MMR Button ONLY when on MabarRekapPage */}
          {isRekapMainPage && (
            <div className="flex items-center gap-2 flex-shrink-0">
              {rekapState.isFinalizedGame ? (
                <button
                  type="button"
                  disabled
                  className="bg-emerald-100 border border-emerald-300 text-emerald-800 font-semibold text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full opacity-80 cursor-not-allowed"
                >
                  <Icon icon="ph:check-circle-bold" className="w-3.5 h-3.5 text-emerald-600" />
                  <span>✅ MMR Final</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new Event('sporto-trigger-push-mmr'))}
                  disabled={rekapState.isPushingMmr}
                  className="bg-gradient-to-r from-[#FF8552] to-[#FC480D] hover:from-[#FC480D] hover:to-[#e03d07] text-white font-semibold text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow-xs cursor-pointer active:scale-95 transition-all"
                >
                  <Icon icon="ph:lightning-bold" className="w-3.5 h-3.5" />
                  <span>{rekapState.isPushingMmr ? 'Memproses...' : 'Push MMR'}</span>
                </button>
              )}
            </div>
          )}

          {/* Right Side: Invite Member Button ONLY when on CommunityMembersPage */}
          {location.pathname.startsWith('/host/members') && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event('sporto-trigger-invite-member'))}
                className="bg-white hover:bg-gray-50 active:scale-95 text-[#FC480D] font-semibold text-xs flex items-center gap-1.5 px-3.5 py-1.5 rounded-full shadow-xs transition-all cursor-pointer"
              >
                <Icon icon="ph:user-plus-bold" className="w-3.5 h-3.5 text-[#FC480D]" />
                <span>Undang</span>
              </button>
            </div>
          )}

          {/* Right Side: Cashflow Export Button */}
          {(location.pathname.startsWith('/host/cash') || location.pathname.startsWith('/cash')) && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event('sporto-cashflow-export'))}
                className="w-9 h-9 rounded-xl bg-gray-50 border border-gray-200/80 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
                aria-label="Export / Laporan"
              >
                <Icon icon="ph:export-bold" className="w-4.5 h-4.5" />
              </button>
            </div>
          )}
        </div>
      </header>
    );
  }

  // Fallback for Admin routes
  return (
    <header className="fixed top-0 left-0 right-0 z-30 bg-white border-b pt-safe md:hidden">
      <div className="h-[52px] flex items-center justify-center px-2">
        <div className="h-[28px] w-full flex items-center justify-between px-2">
          <div className="flex items-center gap-3">
            <div
              role="button"
              tabIndex={0}
              aria-label="Go to home"
              className="w-8 h-8 rounded flex items-center justify-center text-sm font-semibold cursor-pointer"
              onClick={() => {
                navigate('/');
              }}
            >
              <img src="https://cdn.sporto.id/sporto-small.svg" alt="Sporto" className="w-7 h-7" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative group">
              <button
                className="text-primary-500 flex items-center"
                onClick={() => setShowProfileMenu(!showProfileMenu)}
              >
                <Icon icon="mdi:account-circle-outline" className="w-6 h-6" />
              </button>

              {showProfileMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowProfileMenu(false)}></div>
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-md shadow-lg border z-50 py-1">
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      onClick={() => {
                        navigate(`${getAdminBasePath()}/profile-settings`);
                        setShowProfileMenu(false);
                      }}
                    >
                      <Icon icon="mdi:account-cog-outline" className="w-4 h-4" />
                      Profile Settings
                    </button>
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      onClick={async () => {
                        setShowProfileMenu(false);
                        await logout();
                      }}
                    >
                      <Icon icon="mdi:logout" className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {hasChildren && (
        <nav className="h-12 w-full flex overflow-x-auto no-scrollbar bg-white border-t border-gray-100">
          <div className="flex w-full px-4 gap-6">
            {activeParent!.children!.map((child) => {
              const isActive = location.pathname === child.path;
              return (
                <button
                  key={child.path}
                  onClick={() => navigate(child.path)}
                  className={`relative flex items-center justify-center text-sm font-medium whitespace-nowrap py-3 transition-colors ${
                    isActive
                      ? 'text-primary-600 border-b-2 border-primary-600 z-10'
                      : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {child.label}
                </button>
              );
            })}
          </div>
        </nav>
      )}
    </header>
  );
};

export default MobileTopbar;
